import React, { useState } from "react";
import { motion } from "framer-motion";
import { FaGithub, FaExternalLinkAlt } from "react-icons/fa";

// Import project images
import delve from "./images/delve.png";
import Starlite from "./images/starlite-landing.png";
import hyperbook from "./images/hypebook.png";
import tessaImage from "./images/tessa.png";
import fordestImage from "./images/fordest.png";
import hypertick from "./images/hypertick3t.jpeg";
import eduitImage from "./images/eduit.jpg";
import echonodeImage from "./images/echonode.png";
import edennImage from "./images/edenn.png";

const projects = [
  {
    name: "Tessa – AI Support Platform",
    description:
      "Consumer support platform that integrates with helpdesks to resolve inquiries. Launch-ready interface with credits system and real-time agent monitoring.",
    image: tessaImage,
    githubLink: "",
    liveLink: "https://tessahq.com/",
    flag: "🇬🇧",
    country: "UK · SaaS",
    tags: ["AI", "SaaS", "Real-time"],
    featured: true,
  },
  {
    name: "Edenn – Gaming Talent Hub",
    description:
      "A comprehensive talent ecosystem for the gaming industry. Connects studios, gamers, and developers within a high-performance gaming economy.",
    image: edennImage,
    githubLink: "",
    liveLink: "https://edenn.io/",
    flag: "🇺🇸",
    country: "USA · Gaming",
    tags: ["GameDev", "Talent", "Web3"],
    featured: true,
  },
  {
    name: "EchoNode – Hyperliquid Bot",
    description:
      "The winning notification system for the Hyperliquid community hackathon. Delivers real-time trade alerts, liquidations, and deep-dive analytics specifically for HL traders.",
    image: echonodeImage,
    githubLink: "",
    liveLink: "https://echonode.xyz/",
    flag: "🏆",
    country: "Hackathon Winner",
    tags: ["Web3", "Hyperliquid", "Real-time"],
    featured: true,
  },
  {
    name: "Eduit – School Management",
    description:
      "A comprehensive, industrial-scale school management system. Led the engineering on this project at Fordest to deliver a robust platform for modern educational institutions.",
    image: eduitImage,
    githubLink: "",
    liveLink: "https://eduit.app/",
    staticPreview: true,
    flag: "🇳🇬",
    country: "Nigeria · LMS",
    tags: ["Education", "Scale", "Next.js"],
    featured: true,
  },
  {
    name: "HyperBook Documentation Hub",
    description:
      "A sophisticated open-source documentation platform and articles module built for the Hyperliquid community. Shipped as a technical hub for hosting ecosystem-wide docs.",
    image: hyperbook,
    githubLink: "",
    liveLink: "https://www.hypebook.xyz/",
    flag: "🐙",
    country: "Open Source",
    tags: ["Open Source", "Next.js", "Technical Docs"],
    featured: false,
  },
  {
    name: "HyperTick3t – NFT Ticketing",
    description:
      "NFT ticketing built on Hyperliquid. Mint event tickets straight to your wallet, keep memories, and enable readable, collectible event passes.",
    image: hypertick,
    githubLink: "",
    liveLink: "",
    twitterLink: "https://x.com/HyperTick3t",
    comingSoon: true,
    flag: "🌐",
    country: "Web3",
    tags: ["NFTs", "Blockchain"],
  },
  {
    name: "Fordest Technologies Studio",
    description:
      "Full-service product studio shipping scalable web and mobile applications from concept to deployment. Minimal, high-performance agency surface.",
    image: fordestImage,
    githubLink: "",
    liveLink: "http://fordestech.com/",
    flag: "🇳🇬",
    country: "Nigeria · Studio",
    tags: ["Agency", "Mobile", "Web"],
  },
  {
    name: "Starlite Care Org",
    description:
      "Modern professional site for a caregiver organization in the US. Focused on accessibility and high-conversion service layouts.",
    image: Starlite,
    githubLink: "",
    liveLink: "https://starlite-care.vercel.app/",
    flag: "🇺🇸",
    country: "USA · Healthcare",
    tags: ["Healthcare", "CRO"],
  },
  {
    name: "Delve — Interactive AI Learning",
    description:
      "A language-learning product built during the HNG internship. Focused on interactive UX and real-time feedback loops.",
    image: delve,
    githubLink: "https://github.com/",
    liveLink: "https://delve.fun/",
    flag: "🇳🇬",
    country: "Nigeria · EdTech",
    tags: ["AI", "React", "Node.js"],
  },
];



const ProjectCard = ({ project, index }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <motion.div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className={`group relative flex flex-col mb-24 lg:mb-40 ${
        index % 2 === 0 ? "lg:flex-row" : "lg:flex-row-reverse"
      } gap-10 lg:gap-20`}
    >
      {/* Visual Component */}
      <motion.div
        initial={{ opacity: 0, x: index % 2 === 0 ? -60 : 60 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true, amount: 0.15 }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
        className="lg:w-[60%] relative aspect-[16/10] overflow-hidden bg-black/[0.03] dark:bg-white/[0.03] rounded-2xl group/image"
      >
        {/* Subtle Index */}
        <div className="absolute top-6 left-6 z-20 text-[11px] font-mono tracking-widest opacity-30 select-none">
          {"//"} 0{index + 1}
        </div>

        {/* Browser Mockup Shell */}
        <div className="absolute top-0 left-0 w-full h-8 bg-black/5 dark:bg-white/5 border-b border-black/[0.03] dark:border-white/[0.03] flex items-center px-4 gap-1.5 z-20">
          <div className="w-1.5 h-1.5 rounded-full bg-black/10 dark:bg-white/10" />
          <div className="w-1.5 h-1.5 rounded-full bg-black/10 dark:bg-white/10" />
          <div className="w-1.5 h-1.5 rounded-full bg-black/10 dark:bg-white/10" />
        </div>

        {project.liveLink && !project.staticPreview ? (
          <div className="absolute inset-x-0 top-8 bottom-0 overflow-hidden">
            {project.image && (
              <img
                src={project.image}
                alt=""
                aria-hidden
                className="absolute inset-0 w-full h-full object-cover"
              />
            )}
            <motion.div
              animate={{ scale: hovered ? 1.02 : 1 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className={`absolute inset-0 origin-center ${hovered ? "pointer-events-auto" : "pointer-events-none"}`}
            >
              <iframe
                src={project.liveLink}
                title={project.name}
                loading="lazy"
                sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
                className="absolute top-0 left-0 border-0 bg-white"
                style={{
                  width: "200%",
                  height: "200%",
                  transform: "scale(0.5)",
                  transformOrigin: "0 0",
                }}
              />
            </motion.div>
          </div>
        ) : project.image ? (
          <motion.img
            animate={{
              scale: hovered ? 1.03 : 1,
              filter: hovered ? "grayscale(0%)" : "grayscale(20%)",
            }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            src={project.image}
            alt={project.name}
            className="w-full h-full object-cover mt-8"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center font-mono text-[10px] opacity-20 uppercase tracking-[0.2em]">
            Engineering Preview
          </div>
        )}

        {/* Status Marker */}
        <div className="absolute right-6 bottom-6 z-20 overflow-hidden">
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: hovered ? 0 : "100%" }}
            className="bg-accentClr dark:bg-accentClrDark text-white px-4 py-1 text-[10px] font-mono uppercase tracking-widest"
          >
            {project.country}
          </motion.div>
        </div>
      </motion.div>

      {/* Content Component */}
      <motion.div
        initial={{ opacity: 0, x: index % 2 === 0 ? 40 : -40 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true, amount: 0.15 }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.25 }}
        className="lg:w-[40%] flex flex-col justify-center"
      >
        <div className="flex flex-col gap-6">
          <motion.div 
            animate={{ x: hovered ? 10 : 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
          >
            <h3 className="text-4xl lg:text-5xl font-semibold tracking-tight leading-none mb-2">
              {project.name.split(" – ").map((part, i) => (
                <span key={i} className={i === 1 ? "text-accentClr dark:text-accentClrDark italic font-serif block mt-1" : ""}>
                  {part}
                </span>
              ))}
            </h3>
          </motion.div>

          <p className="text-[16px] opacity-60 leading-relaxed font-normal max-w-sm">
            {project.description}
          </p>

          <div className="flex flex-wrap gap-x-6 gap-y-2 mt-2">
            {project.tags.map((t) => (
              <span key={t} className="text-[10px] font-mono uppercase tracking-widest opacity-40">
                # {t}
              </span>
            ))}
          </div>

          <div className="flex items-center gap-6 mt-4">
            {project.liveLink && (
              <a 
                href={project.liveLink} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="flex items-center gap-2 group/link"
              >
                <span className="text-[13px] font-bold border-b border-black dark:border-white pb-0.5 group-hover/link:border-accentClr dark:group-hover/link:border-accentClrDark group-hover/link:text-accentClr dark:group-hover/link:text-accentClrDark transition-all">
                  Launch Platform
                </span>
                <FaExternalLinkAlt size={12} className="opacity-40 group-hover/link:text-accentClr dark:group-hover/link:text-accentClrDark" />
              </a>
            )}
            {project.githubLink && (
              <a 
                href={project.githubLink} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-[12px] opacity-50 hover:opacity-100 transition-opacity flex items-center gap-2"
              >
                <FaGithub size={16} />
                <span>Repository</span>
              </a>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

const Projects = () => {
  return (
    <section id="projects" className="text-textClr w-full py-20 lg:py-40 dark:text-textClrDark overflow-hidden">
      <div className="lg:w-[75%] w-[90%] mx-auto flex flex-col">
        <div className="mb-24 lg:mb-40">
          <motion.p initial={{ opacity: 0, x: -10 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="text-xs tracking-[0.25em] uppercase opacity-50 font-medium font-mono">
            {"//"} Selected work
          </motion.p>
          <h2 className="text-5xl lg:text-8xl font-semibold leading-[0.9] tracking-tight mt-6">
            Products I've <span className="text-accentClr dark:text-accentClrDark italic font-serif">shipped</span>.
          </h2>
        </div>

        <div className="flex flex-col w-full">
          {projects.map((project, index) => (
            <ProjectCard key={index} project={project} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Projects;
